import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";

// Platform detection
export type Platform = "web" | "overwolf" | "mobile";

// Viewport sizes matching our breakpoints
export type ViewportSize = "mobile" | "tablet" | "desktop" | "desktop-lg";

// Gaming context
export type GameContext = "in-game" | "lobby" | "idle";

export interface DeviceContext {
    platform: Platform;
    viewportSize: ViewportSize;
    gameContext: GameContext;
    isInGame: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    width: number;
    height: number;
}

// Breakpoint values from _responsive.scss
const BREAKPOINTS = {
    mobile: 375,
    tablet: 768,
    desktop: 1280,
    desktopLg: 1440,
} as const;

// Detect the current platform
const detectPlatform = (): Platform => {
    // Check for Overwolf
    if (typeof window !== "undefined") {
    // Check actual Overwolf API presence
        if ((window as any).overwolf) {
            return "overwolf";
        }

        // Check session storage flag (set by Overwolf iframe)
        if (window.sessionStorage.getItem("isOnOverwolf") === "true") {
            return "overwolf";
        }

        // Check URL parameter for testing
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("overwolf") === "true") {
            return "overwolf";
        }
    }

    // Check for mobile user agent
    if (typeof window !== "undefined" && (/iPhone|iPad|iPod|Android/i).test(navigator.userAgent)) {
        return "mobile";
    }

    return "web";
};

// Detect viewport size based on width
const getViewportSize = (width: number): ViewportSize => {
    if (width < BREAKPOINTS.tablet) return "mobile";
    if (width < BREAKPOINTS.desktop) return "tablet";
    if (width < BREAKPOINTS.desktopLg) return "desktop";
    return "desktop-lg";
};

// No longer needed as standalone - will use Redux

export const useDeviceContext = (): DeviceContext => {
    const [dimensions, setDimensions] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 1280,
        height: typeof window !== "undefined" ? window.innerHeight : 720,
    });

    const [platform] = useState<Platform>(detectPlatform());

    // Get game info from Redux store
    const gameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);

    // Determine game context based on Redux state
    const gameContext: GameContext = gameInfo?.isRunning ? "in-game" : "idle";

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const viewportSize = getViewportSize(dimensions.width);

    return {
        platform,
        viewportSize,
        gameContext,
        isInGame: gameContext === "in-game",
        isMobile: viewportSize === "mobile",
        isTablet: viewportSize === "tablet",
        isDesktop: viewportSize === "desktop" || viewportSize === "desktop-lg",
        width: dimensions.width,
        height: dimensions.height,
    };
};

// Hook to apply context classes to body
export const useDeviceContextClasses = () => {
    const context = useDeviceContext();

    useEffect(() => {
        const body = document.body;

        // Clear existing classes
        body.classList.remove(
            "platform-web",
            "platform-overwolf",
            "platform-mobile",
            "context-in-game",
            "context-lobby",
            "context-idle",
            "viewport-mobile",
            "viewport-tablet",
            "viewport-desktop",
            "viewport-desktop-lg",
        );

        // Add current classes
        body.classList.add(`platform-${context.platform}`);
        body.classList.add(`context-${context.gameContext}`);
        body.classList.add(`viewport-${context.viewportSize}`);
    }, [context.platform, context.gameContext, context.viewportSize]);

    return context;
};

// Export constants for use in components
export const PLAY_BREAKPOINTS = BREAKPOINTS;
export const PLAY_MOBILE = BREAKPOINTS.mobile;
export const PLAY_TABLET = BREAKPOINTS.tablet;
export const PLAY_DESKTOP = BREAKPOINTS.desktop;
export const PLAY_DESKTOP_LG = BREAKPOINTS.desktopLg;